/**
 * colors.ts - THE SINGLE SOURCE OF TRUTH FOR COLORS
 *
 * To add a new color:
 * 1. Add it to `palette` (raw color value) or `semantic` (references palette)
 * 2. Run `yarn generate:css-properties` to update custom-properties.css
 * That's it! Types are automatic.
 *
 * NOTE: The order of properties determine their order in the generated CSS.
 */

// Note: Moving away from using TagLt colors for tags

// PALETTE - Raw color values
export const palette = {
    grey0: '#ffffff',
    grey50: '#f7f7f7',
    grey100: '#f1f1f1',
    grey200: '#ebe8e8',
    grey300: '#dbd9d9',
    grey400: '#d4d1cf',
    grey500: '#a6a3a1',
    grey600: '#7a7370',
    grey700: '#4d4540',
    grey800: '#211712',
    grey900: '#000000',

    offWhiteTransparent: 'rgba(247, 247, 247, 0)',
    modalOverlay: 'hsla(234, 18%, 25%, 0.67)',

    red50: '#fff7f7',
    red100: '#ffe5e7',
    red200: '#ffd6d9',
    red300: '#ffb2bb',
    red400: '#ff596b',
    red500: '#ff2e47',
    red600: '#db293e',
    red700: '#bf2436',
    red800: '#73121d',
    red900: '#4d0c13',

    orange50: '#fff7f5',
    orange100: '#ffeee5',
    orange150: '#ffdecc',
    orange200: '#ffccb2',
    orange300: '#ffae80',
    orange400: '#ff7833',
    orange500: '#fe5800',
    orange600: '#ea7134',
    orange700: '#bf4200',
    orange800: '#8e3200',
    orange900: '#662400',

    yellow50: '#fffcf7',
    yellow100: '#fff8e5',
    yellow150: '#fff5d9',
    yellow200: '#fff0c5',
    yellow300: '#fde9b4',
    yellow400: '#ffde73',
    yellow500: '#fac947',
    yellow600: '#e1b233',
    yellow700: '#bd9633',
    yellow800: '#a37d23',
    yellow900: '#7e6019',

    green50: '#f7fcf7',
    green100: '#ecf9ef',
    green200: '#d6f2de',
    green300: '#c4eecc',
    green400: '#5ccf73',
    green500: '#33c252',
    green600: '#2e9942',
    green700: '#0b8d6c',
    green800: '#165024',
    green900: '#113c1b',

    navy50: '#ebf5fb',
    navy100: '#d8ecf7',
    navy200: '#c0e0f2',
    navy300: '#7d9ace',
    navy400: '#5977a4',
    navy500: '#0c57ad',
    navy600: '#065289',
    navy750: '#1c2c48',
    navy800: '#1c293f',
    navy900: '#142034',

    nutshellPro: '#4f86c6',
    dotcomTeal: '#62d4ca',

    blue50: '#e8f4fa',
    blue100: '#d9edf7',
    blue150: '#c0e0f2',
    blue200: '#b1d8ef',
    blue300: '#94d1fe',
    blue400: '#61b2de',
    blue500: '#3b9ed7',
    blue600: '#337da8',
    blue700: '#235f81',
    blue800: '#183f56',
    blue900: '#0c202b',

    plum50: '#e9e9f6',
    plum100: '#dbdbf2',
    plum200: '#c8c8e9',
    plum300: '#acace1',
    plum400: '#7373c7',
    plum500: '#514fb8',
    plum600: '#3d3b8a',
    plum700: '#292967',
    plum800: '#20204a',
    plum900: '#101025',

    purple10: '#fbfaff',
    purple50: '#f2eefb',
    purple100: '#e6ddf7',
    purple200: '#e2a9ff',
    purple300: '#bb97f6',
    purple400: '#a378f6',
    purple500: '#8b5cf6',
    purple600: '#7744e1',
    purple700: '#602bcc',
    purple800: '#4d22a3',
    purple900: '#321273',

    dotcomPurple: '#660347',

    slate100: '#eeeff4',
    slate200: '#e5e6ee',
    slate300: '#c8cadb',
    slate400: '#9397ba',
    slate500: '#5c5f87',
    slate550: '#3f4372',
    slate600: '#3d405c',
    slate650: '#333557',
    slate700: '#333766',
    slate800: '#242854',
    slate900: '#0c0f33',
    slate950: '#0a0d2b',

    // Social platform/integrations colors
    messengerBlue: '#3b5998',
    instagramPink: '#dd2a7b',
    whatsappGreen: '#25d366',
    whatsappGreenDk: '#075e54',
    zoomBlue: '#0b5cff',
    googleGreen: '#05ac47',
    teamsBlue: '#464eb8',
    socialTwitter: '#00aced',
    socialLinkedin: '#007bb6',
    socialFoursquare: '#ef4b78',
    socialPinterest: '#cb2027',
    socialYoutube: '#e52d27',
    socialFlickr: '#ff0084',
    socialVimeo: '#aad450',
    socialGooglePlus: '#dd4b39',
    socialTumblr: '#35465c',
    socialDribbble: '#ea4c89',
    socialBehance: '#1769ff',
    socialDelicious: '#3399ff',
    socialHackerNews: '#ff6600',
    socialInstagram: '#3f729b',
    socialLastfm: '#c3000d',
    socialReddit: '#ff4500',
    socialTwitch: '#6441a5',
    socialVine: '#00b488',
} as const;

// SEMANTIC - Colors that reference the palette
export const semantic = {
    // Core colors
    white: 'grey0',
    offWhite: 'grey50',
    offWhiteMd: 'grey200',
    offWhiteDk: 'grey200',
    greyXlt: 'grey400',
    greyLt: 'grey600',
    grey: 'grey700',
    greyDk: 'grey800',
    black: 'grey900',
    roseXlt: 'red50',
    roseLt: 'red200',
    redLt: 'red200',
    rose: 'red500',
    red: 'red500',
    roseDk: 'red700',
    orangeXlt: 'orange100',
    orangeBg: 'orange100',
    orangeLt: 'orange150',
    orange: 'orange500',
    orangeAttention: 'orange600',
    orangeDk: 'orange700',
    brownLt: 'yellow150',
    brown: 'yellow200',
    brownDk: 'yellow700',
    yellowBg: 'yellow50',
    yellowLt: 'yellow150',
    yellow: 'yellow500',
    greenBg: 'green50',
    greenXlt: 'green100',
    greenLt: 'green200',
    green: 'green500',
    greenDk: 'green600',
    blueBgLt: 'blue50',
    blueBg: 'blue100',
    blueXlt: 'blue50',
    blueLt: 'blue100',
    blue: 'blue500',
    blueDk: 'blue600',
    navyBg: 'blue50',
    navyLt: 'blue100', // Navy lt is deprecated
    navyXltNew: 'blue300',
    navyLtNew: 'navy400',
    navy: 'blue600',
    navyDk: 'blue600',
    navyBgActiveNew: 'slate500',
    navyDkNew: 'slate600',
    navyBgNew: 'slate700',
    aiBlue: 'slate900',
    aiHover: 'slate950',
    plumXlt: 'plum50',
    plumBg: 'plum50',
    plumLt: 'plum100',
    plumBgDk: 'plum300',
    plum: 'plum600',
    plumDk: 'plum600',
    aiPurplePinkXxlt: 'purple10',
    aiPurplePinkXlt: 'purple50',
    aiPurplePinkLt: 'purple100',
    aiPurplePinkMd: 'purple200',
    aiPurplePink: 'purple300',
    aiPurpleXlt: 'purple400',
    aiPurpleLt: 'purple500',
    aiPurpleMd: 'purple600',
    aiPurplePrimary: 'purple700',
    aiPurpleDk: 'purple800',

    // Tag colors
    tagGreyLt: 'grey200',
    tagGrey: 'grey400',
    tagGreyDk: 'grey600',
    tagRedLt: 'red100',
    tagRed: 'red300',
    tagRedDk: 'red500',
    tagOrangeLt: 'orange100',
    tagOrange: 'orange200',
    tagOrangeDk: 'orange400',
    tagYellowLt: 'yellow100',
    tagYellow: 'yellow300',
    tagYellowDk: 'yellow500',
    tagGreenLt: 'green100',
    tagGreen: 'green300',
    tagGreenDk: 'green400',
    tagBlueLt: 'blue50',
    tagBlue: 'blue150',
    tagBlueDk: 'blue500',
    tagPurpleLt: 'plum50',
    tagPurple: 'plum200',
    tagPurpleDk: 'plum400',

    // Status colors
    alertBg: 'red50',
    alert: 'red700',
    overdue: 'red700',
    error: 'red500',
    warningBg: 'yellow100',
    warning: 'orange700',
    successBg: 'green100',
    success: 'green700',
    infoBg: 'blue50',
    info: 'blue600',
    support: 'plum600',
    upgrade: 'slate900',

    // Text colors
    placeholder: 'grey500',
    label: 'grey700',
    textHot: 'orange500',
    textUsers: 'orange700',
    textAccounts: 'plum600',
    textContacts: 'blue600',

    // Record colors
    activity: 'red700',
    task: 'yellow700',
    product: 'yellow700',
    productBg: 'yellow700',
    productBgHovered: 'yellow500',
    lead: 'green600',
    leadHovered: 'green700',
    person: 'blue600',
    company: 'plum500',

    // Icon colors
    iconPrimary: 'grey700',
    iconPrimaryHovered: 'grey800',
    iconSecondary: 'grey400',
    iconSecondaryDk: 'grey600',
    iconPrimaryAction: 'grey400',
    iconPrimaryActionHovered: 'blue400',

    // Sidebar colors
    secondarySidebarText: 'slate400',
    secondarySidebarHoverBlue: 'slate550',
    sidebarListLoading: 'slate650',
    sidebarSecondary: 'slate800',
    sidebarPrimary: 'slate900',

    // Other
    zebraStripe: 'grey50',
    skeletonGrey: 'grey100',
    buttonSecondary: 'grey200',
    buttonSecondaryDk: 'grey300',
    formBorder: 'grey400',
    wallboardRed: 'red50',
    backgroundHot: 'orange500',
    bannerBorderOrange: 'orange600',
    quoteSignatureOrange: 'orange600',
    listSelected: 'blue50',
    listHovered: 'blue100',
    focusBlue: 'navy600',
    supportHover: 'plum700',
    sidebarRed: 'slate900',

    // Onboarding colors
    onboardingHelpLt: 'plum400',
    onboardingHelp: 'plum500',
    onboardingHelpDk: 'plum600',

    // RCFX colors
    rcfxSidebarActive: 'navy500',
    rcfxSidebar: 'navy750',
    rcfxSidebarHover: 'navy800',
    rcfxSidebarPrimaryItem: 'navy900',

    // Report colors
    reportRed: 'red400',
    reportOrangeBg: 'orange50',
    reportOrange: 'orange400',
    reportGreen: 'green400',
    reportBlue: 'blue400',
    reportNavy: 'slate600',

    // Social platform colors
    socialX: 'grey900',
    socialAngelList: 'grey900',
    socialDigg: 'grey900',
    socialGithub: 'grey900',
    socialFacebook: 'messengerBlue',

    // AI Purple
    aiPurpleBg: 'purple10',
    aiPurpleBorder: 'purple400',
    aiPurpleBoxShadow: 'purple100',

    // AI Purple gradients - basic
    aiPurpleGradientBasicOne: 'purple500',
    aiPurpleGradientBasicTwo: 'purple700',

    // AI Purple gradients - regular
    aiPurpleGradientOne: 'purple300',
    aiPurpleGradientTwo: 'purple500',
    aiPurpleGradientThree: 'purple700',

    // AI Purple gradients - expanded
    aiPurpleGradientExpandedOne: 'purple200',
    aiPurpleGradientExpandedTwo: 'purple300',
    aiPurpleGradientExpandedThree: 'purple500',
    aiPurpleGradientExpandedFour: 'purple700',

    // AI Purple gradients - bold
    aiPurpleGradientBoldOne: 'purple200',
    aiPurpleGradientBoldTwo: 'purple600',
    aiPurpleGradientBoldThree: 'purple500',
    aiPurpleGradientBoldFour: 'purple200',
    aiPurpleGradientBoldFive: 'purple500',
    aiPurpleGradientBoldSix: 'purple600',
} as const;

function isPaletteKey(value: string): value is PaletteKey {
    return value in palette;
}

const resolvedSemantic: {[K in SemanticKey]: string} = {} as {
    [K in SemanticKey]: string;
};

for (const [key, value] of Object.entries(semantic)) {
    (resolvedSemantic as Record<string, string>)[key] = isPaletteKey(value)
        ? palette[value]
        : value;
}

export type PaletteKey = keyof typeof palette;
export type SemanticKey = keyof typeof semantic;
export type ColorName = keyof typeof colors;

export const colors = {
    ...palette,
    ...resolvedSemantic,
} as const;

export const colorDefinitions = {
    palette,
    semantic,
};
